import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { dataActions } from '@/store/actions/data.actions';
import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import CustomSelect from '@/views/components/CustomSelect/CustomSelect';

import LettersNameAutoSuggest from '@/views/components/AutoSuggestInput/LettersNameAutoSuggest';
import LettersDeptAutoSuggest from '@/views/components/AutoSuggestInput/LettersDeptAutoSuggest';

import SearchInput from '@/views/components/SearchInput/SearchInput';

import styles from '@/views/pages/Templates/Templates.module.scss';

const LetterTemplatesSearchFilters = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { dict, mode } = useSelector((state) => state.app);
	const user = useSelector((state) => state.auth.user);

	// load title types here - it will be needed in LettersItem
	const { categories } = useSelector(state => state.data);
	const [ selectCategories, setSelectCategories ] = useState([]);

	const [ categoryStartValue, setCategoryStartValue ] = useState({ value: 'none', label: 'Wszystkie kategorie' });
	const [ departmentStartId, setDepartmentStartId ] = useState(0);

	const [ searchedQuery, setSearchedQuery ] = useState('');
	const [ query, setQuery ] = useState('');

	const [ hideFilters, setHideFilters ] = useState(false);

	const isMobile = useWindowIsMobile(767);
	
	const userRole = user && user.role;

	const resetAllFilters = () => {
		setCategoryStartValue({ label: dict.letters.allCategories, value: 'none' });
		setDepartmentStartId(null);
	};

	const searchQuery = (searchString = null) => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (searchString === null || searchString.length === 0) {
			if (Object.keys(copiedSearchParams).indexOf('q') !== -1) {
				delete copiedSearchParams.q;
				if (copiedSearchParams.ord === '-accuracy') { copiedSearchParams.ord = '-modifiedOn'; }
				navigate(`${location.pathname}?${parseObjectToQuery(copiedSearchParams)}`);
			}
		} else {
			resetAllFilters();
			delete copiedSearchParams.ord;
			navigate(`${location.pathname}?q=${encodeURIComponent(searchString)}&ord=-accuracy&page=1`);
		}
	};

	useEffect(() => {
		if (!categories) dispatch(dataActions.getLetterCategories());
		const qu = queryString.parse(location.search);
		if (Object.keys(qu).indexOf('q') !== -1) {
			setQuery(qu.q);
			setSearchedQuery(qu.q);
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (copiedSearchParams.q) {
			setQuery(copiedSearchParams.q);
			setSearchedQuery(copiedSearchParams.q);
		} else {
			setQuery("");
			setSearchedQuery("");
		}
	}, [ location ]);

	useEffect(() => {
		if (categories) {
			const qu = queryString.parse(location.search);
			let urlCategory = null;
			if (Object.keys(qu).indexOf('category') !== -1) {
				// there is version in url, set start value of recipient select
				urlCategory = qu.category;
			}
			setSelectCategories([ { label: dict.letters.allCategories, value: 'none' },
				...categories.map(item => {
					const setItem = { value: item.code, label: item.label };
					if (urlCategory && item.code === urlCategory) {
						setCategoryStartValue(setItem);
					}
					return setItem;
				},
				) ]);
		}
	}, [ categories ]);

	const onSelectCategory = ({ value, label }) => {
		setCategoryStartValue({ value, label });
		const qu = queryString.parse(location.search);
		if (value === 'none') {
			if (Object.keys(qu).indexOf('category') !== -1) {
				delete qu.category;
				navigate(`${location.pathname}?${parseObjectToQuery(qu)}`);
			}
		} else {
			navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, category: value })}`);
		}
	};

	useEffect(() => {
		if(queryString.parse(location.search)?.department) {
			setDepartmentStartId(queryString.parse(location.search)?.department || 0);
		}

		// eslint-disable-next-line
	}, [ location.search ]);

	return (
		<section className={ styles['letter-templates__searchbar'] }>
			<div className={ styles['letter-templates__searchbar--item'] }>
				<SearchInput
					label={ dict.templates.searchLabel }
					placeholder={ "" }
					searchedValue={ searchedQuery }
					value={ query }
					setValue={ setQuery }
					searchFunction={ searchQuery }
				/>
			</div>

			<div className={ styles['letter-templates__searchbar--item'] }>
				{(!hideFilters || !isMobile) && <div className={ `${ styles['letter-templates__searchbar--filters'] } ${!CanHandleDepartments(userRole, mode) ? styles['letter-templates__searchbar--filters-standalone'] : null }` }>
					{CanHandleDepartments(userRole, mode) && <LettersDeptAutoSuggest
						label={ dict.letters.searchByDepartment }
						placeholder={ dict.letters.autocompletePlaceholder }
						tooltipIcon={ dict.templates.defaultDeptTooltip }
						startDepartmentId={ departmentStartId }
					/>}

					<LettersNameAutoSuggest
						label={ dict.letters.searchByInitiator }
						placeholder={ dict.letters.autocompletePlaceholder }
					/>

					{selectCategories && (
						<CustomSelect
							label={ dict.letter.categories.category }
							options={ selectCategories }
							value={ categoryStartValue }
							onChange={ onSelectCategory }
							placeholder={ dict.letter.categories.category }
						/>
					)}
				</div>}
				{isMobile && <div
					className={ styles['letter-templates__searchbar--hide-button'] }
					onClick={ () => {
						setHideFilters(!hideFilters);
					} }
				>
					{ !hideFilters ?
						<>Ukryj filtry <i className="icon-arrow-up-full" /></> :
						<>Pokaż filtry <i className="icon-arrow-down-full" /></>
					}
				</div>}
			</div>
		</section>
	);
};

export default memo(LetterTemplatesSearchFilters);
